import SafeImage from "@/components/ui/SafeImage";
import VideoPlayerV3 from "@/components/ui/VideoPlayerV3";
import VideoPlayerV4 from "@/components/ui/VideoPlayerV4";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import {
  FacebookIcon,
  InstagramIcon,
  MessageCircle,
  Play,
  Heart,
  TrendingUp,
} from "lucide-react";
import { useCallback, useState } from "react";

const ProductCard = ({
  video,
  index,
  currentlyPlaying,
  setCurrentlyPlaying,
  isBrandVideo = false,
  setIsDetailsModalOpen,
  setSelectedVideo,
}: {
  setIsDetailsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentlyPlaying: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedVideo?: React.Dispatch<React.SetStateAction<VideoData | null>>;
  currentlyPlaying: string | null;
  video: VideoData;
  index?: number;
  isBrandVideo?: boolean;
}) => {
  // Format numbers for display (e.g., 1.2K, 10.5M)
  const formatNumber = (num: number | string): string => {
    const numValue =
      typeof num === "string" ? parseInt(num.replace(/,/g, "")) : num;
    if (isNaN(numValue)) return "0";

    if (numValue >= 1000000) {
      return (numValue / 1000000).toFixed(1) + "M";
    } else if (numValue >= 1000) {
      return (numValue / 1000).toFixed(1) + "K";
    }
    return numValue.toString();
  };

  const [isMuted, setIsMuted] = useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = useState("");

  const handleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const handlePlayVideo = useCallback((url: string) => {
    console.log("Playing URL:", url); // Log the URL being played
    setPlayingVideoUrl((prev) => (prev === url ? "" : url));
  }, []);

  const handlerOpenDetailsModal = () => {
    if (setIsDetailsModalOpen && setSelectedVideo) {
      setSelectedVideo(video);
      setCurrentlyPlaying("");
      setIsDetailsModalOpen(true);
    }
  };

  return (
    <div className="h-full">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200 h-full flex flex-col">
        {/* Video Player Section */}
        <div
          className={`${
            isBrandVideo ? "h-32" : "h-40 sm:h-52 "
          } relative bg-gray-200 flex-shrink-0`}
        >
          <VideoPlayerV3
            thumbnailUrl={video.thumbnail_url}
            url={video.video_url}
            currentlyPlaying={currentlyPlaying}
            setCurrentlyPlaying={setCurrentlyPlaying}
            className="rounded-none w-full h-full object-cover"
          />

          {/* Ad/Organic Badge */}
          <div className="absolute z-50 top-3 right-3">
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full backdrop-blur-sm ${
                video.ad_or_organic === "Ad"
                  ? "bg-blue-100/90 text-blue-700 border border-blue-200/50"
                  : "bg-green-100/90 text-green-700 border border-green-200/50"
              }`}
            >
              {video.ad_or_organic}
            </span>
          </div>

          {/* Video Stats Overlay */}
          <div className="absolute z-[999] top-2 left-2 flex flex-col gap-1">
            {video.plays && (
              <div className="flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full">
                <Play className="w-3 h-3 text-white fill-white" />
                <span className="text-xs font-medium text-white">
                  {formatNumber(video.plays)}
                </span>
              </div>
            )}
            {video.likes && (
              <div className="flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full">
                <Heart className="w-3 h-3 text-white fill-white" />
                <span className="text-xs font-medium text-white">
                  {formatNumber(video.likes)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div
          className="p-4 space-y-3 flex-1 flex flex-col"
          onClick={handlerOpenDetailsModal}
        >
          {/* Brand Header */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* {video.page_profile_picture_url ? (
              <img
                src={video.page_profile_picture_url}
                alt={`${video.brand} logo`}
                className="2xl:size-10 size-7 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-100"
              />
            ) : (
              <div className="2xl:size-10 size-7 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0 ring-2 ring-gray-100" />
            )} */}

            {video.page_profile_picture_url ? (
              <SafeImage
                src={video.page_profile_picture_url}
                alt={`${video.brand} logo`}
                className="2xl:size-10 size-7 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-100"
                fallbackComponent={
                  <div className="2xl:size-10 size-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex-shrink-0 ring-2 ring-gray-100 flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {video.brand?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                  </div>
                }
              />
            ) : (
              <div className="2xl:size-10 size-7 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0 ring-2 ring-gray-100" />
            )}

            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                {video.brand}
              </h3>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500 truncate">
                  {video?.published_on}
                </p>
                {/* Platform Icons */}
                <div className="flex gap-1">
                  {video?.published_on?.length > 0 &&
                    video.published_on[0] === "facebook" && (
                      <>
                        {video.published_on.map((platform) => (
                          <div
                            key={platform}
                            className="w-4 h-4"
                            title={platform}
                          >
                            {platform === "Facebook" && (
                              <FacebookIcon className="w-4 h-4 text-blue-600" />
                            )}
                            {platform === "Instagram" && (
                              <InstagramIcon className="w-4 h-4 text-pink-500" />
                            )}
                          </div>
                        ))}
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* Caption */}
          <div className="flex-1">
            <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
              {video.caption}
            </p>
          </div>

          {/* Engagement Stats */}
          <div className="grid grid-cols-2 gap-3 py-2">
            {/* Comments */}
            {video?.comments > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <MessageCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 truncate">Comments</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {video.comments}
                  </p>
                </div>
              </div>
            )}

            {/* Plays (Desktop version for more detail) */}
            {video?.plays && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <Play className="w-4 h-4 text-green-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 truncate">Views</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {formatNumber(video.plays)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Additional Stats Row */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-100 flex-shrink-0">
            {video?.likes && (
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">
                  {formatNumber(video.likes)}
                </span>
                <span className="text-xs text-gray-400">likes</span>
              </div>
            )}

            {video.ad_or_organic !== "Organic" && video.days_since_running && (
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm text-gray-500 truncate">
                  {video.days_since_running}d running
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
